import pegasus from "../../assets/scripts/global/01-pegasus";

/**
 * allModals
 *
 * Component code for creating all modals on the page
 *
 * 	Usage:
 * 	Add link with the attr 'data-pegasus-modal'.
 * 	Any values passed to the attr will be added as classes on the modal so it can be styled accordingly.
 * 	To include modal navigation, pass the class 'Modal--with-nav' to the data attr. This class will be picked up and the nav will be built.
 *
 * 	Example:
 *  <a href="#" data-pegasus-modal="Modal--team Modal--with-nav"></a>
 *
 * @since  2.0.0
 */
window.allModals = () => {
    return {
        init() {
            const _this = this;
            const modalSkeleton = pegasus.stringToHTML(
                document.getElementById("Modal__Skeleton").innerHTML
            );
            const modalLinks = _this.createModalLinks(
                document.querySelectorAll("[data-pegasus-modal]")
            );

            modalLinks.forEach((link, index) => {
                const modal = modalSkeleton.cloneNode(true);

                let cssClass = "Modal",
                    modalNav;

                //check for classes to add
                if (link.dataset.pegasusModal.length) {
                    cssClass += " " + link.dataset.pegasusModal;

                    //if nav specified
                    if (link.dataset.pegasusModal.includes("Modal--with-nav")) {
                        modalNav = _this.buildModalNav(index, modalLinks);

                        if (modalNav) {
                            modal
                                .querySelector(".Modal__navigation")
                                .append(modalNav);
                        }
                    }
                }

                //setup classes and id
                modal.className = cssClass;
                modal.id = link.dataset.pegasusModalId;

                //append modal template to page if it does nt already exist
                if (!document.getElementById(link.dataset.pegasusModalId)) {
                    document.body.append(modal);
                }
            });

            window.modal = this;
        },

        doesLinkAlreadyExist(link, linksArray) {
            return linksArray.find((element) => element.href === link.href);
        },

        createModalLinks(links) {
            const _this = this;
            const modalLinks = [];

            if (links.length) {
                links.forEach((link, index) => {
                    // Add ?modal=yes query string to url, so head and foot are removed from fetched page
                    const href = new URL(link.href);

                    link.href = href;

                    const existingLink = _this.doesLinkAlreadyExist(
                        link,
                        modalLinks
                    );

                    //check for existing link and copy id if true
                    if (existingLink) {
                        link.setAttribute(
                            "data-pegasus-modal-id",
                            existingLink.dataset.pegasusModalId
                        );
                    } else {
                        link.setAttribute(
                            "data-pegasus-modal-id",
                            `modal-${index}`
                        );
                    }

                    link.addEventListener("click", (e) => {
                        e.preventDefault();
                        _this.loadModalContent(
                            link.href,
                            link.dataset.pegasusModalId
                        );
                    });

                    //add event to emit 'modalopen' event for specified modal
                    link.addEventListener("click", _this.emitModalOpen);

                    //check we're not adding existing links.
                    if (!existingLink) modalLinks.push(link);
                });
            }

            return modalLinks;
        },

        emitModalOpen(e) {
            e.preventDefault();

            const modalID = e.currentTarget.dataset.pegasusModalId;
            const myEvent = new CustomEvent("modalopen", {
                detail: { modalID },
            });

            window.dispatchEvent(myEvent);
        },

        loadModalContent(href, modalID) {
            const url = new URL(href);
            url.searchParams.append("modal", "yes");
            pegasus
                .fetchContent(url)
                .then((resp) => {
                    if (!resp.ok) throw Error(resp.statusText);

                    console.log("loading modal content...");

                    return resp.text();
                })
                .then((htmlResponse) => {
                    const modalHTML = htmlResponse;

                    const modalBody = document.querySelector(
                        `#${modalID} .Modal__body`
                    );

                    modalBody.innerHTML = modalHTML;

                    return modalBody;
                })
                .then((modalBody) => {
                    pegasus.decodeAllCloudflareEmails(modalBody);
                    //reinit anything else here...
                    // pegasus.decodeAllCloudflareEmails(modalBody);
                    //reinit cf7 forms
                    if (typeof wpcf7 !== "undefined" && wpcf7 !== null) {
                        const cf7Forms = document.querySelectorAll(
                            `#${modalID} .wpcf7 > form`
                        );
                        if (cf7Forms.length)
                            cf7Forms.forEach((form) => wpcf7.init(form));
                    }
                })
                .catch((error) => {
                    console.error(error);

                    const modalBody = document.querySelector(
                            `#${modalID} .Modal__body`
                        ),
                        errorContainer =
                            modalBody.querySelector(".Modal__error");

                    if (
                        typeof errorContainer === "undefined" ||
                        errorContainer === null
                    )
                        return;

                    errorContainer.classList.remove("hidden");
                })
                .finally(() => {
                    if (document.lazyLoadInstance) {
                        document.lazyLoadInstance.update();
                    }

                    pegasus.decodeAllCloudflareEmails();

                    window.dispatchEvent(new Event("resize"));
                });
        },
        getSubstringBetween(str, startDelimiter, endDelimiter) {
            // Find the start position of the substring
            const startIndex = str.indexOf(startDelimiter);
            if (startIndex === -1) {
                // If the start delimiter is not found, return an empty string or handle error
                return "";
            }

            // Find the end position of the substring
            const endIndex = str.indexOf(
                endDelimiter,
                startIndex + startDelimiter.length
            );
            if (endIndex === -1) {
                // If the end delimiter is not found, return an empty string or handle error
                return "";
            }

            // Extract the substring between the start and end delimiters
            const substring = str.substring(
                startIndex + startDelimiter.length,
                endIndex
            );

            return substring;
        },
    };
};

/**
 * singleModal
 *
 * Component code for a single modal
 *
 * @since  2.0.0
 */
window.singleModal = () => {
    return {
        open: false,

        handleModalOpen(evt) {
            //only run on actual modals, not skeleton
            if (this.$el.classList.contains("ModalSkeleton")) return;

            document.body.classList.add("modal-open");
            this.open = this.$el.id === evt.detail.modalID;
        },

        handleModalClose() {
            document.body.classList.remove("modal-open");
            this.open = false;
        },
    };
};

/*
document.addEventListener('DOMContentLoaded', function () {
    document.addEventListener('wpcf7mailsent', function (event) {
        const formId = event.detail.contactFormId;
        const modalBody = document.querySelector(`#${event.detail.unitTag}`);
        
        if (modalBody) {
            let message = '';
            const siteUrl = window.location.origin;

            switch (formId) {
                case 11152:
                case 11161:
                    message = `<p class='text-white'>We can confirm we've received your CV! Thank you. Should your skills and experience match those required for any of our live vacancies, one of our consultants will be in touch with you shortly.</p>
                    <p class="text-white">Due to the volume of applications we receive, unfortunately we're unable to reply to everyone. If you haven't heard from us within 5 working days then please assume your CV isn't suitable for any current roles but we will be in touch if anything suitable comes our way. (Your CV will never be put forward for a role without your prior consent.)</p>
                    <a class="text-white transition duration-300 !underline hover:opacity-50" href="${siteUrl}/blog/#newsletter">Get jobs straight to your inbox - Sign up to our newsletter and receive a monthly email from us packed with industry news, career tips and brand new opportunities within your chosen sector.</a>`;
                    break;
                case 12110:
                    message = `<p class='text-secondary'>We can confirm we've received your CV! Thank you. Should your skills and experience match those required for any of our live vacancies, one of our consultants will be in touch with you shortly.</p>
                    <p class="text-secondary">Due to the volume of applications we receive, unfortunately we're unable to reply to everyone. If you haven't heard from us within 5 working days then please assume your CV isn't suitable for any current roles but we will be in touch if anything suitable comes our way. (Your CV will never be put forward for a role without your prior consent.)</p>
                    <a class="text-secondary transition duration-300 !underline hover:opacity-50" href="${siteUrl}/blog/#newsletter">Get jobs straight to your inbox - Sign up to our newsletter and receive a monthly email from us packed with industry news, career tips and brand new opportunities within your chosen sector.</a>`;
                    break;
                case 11172:
                    message = `<p class='text-white'><strong>Thanks for subscribing to our job alerts</strong></p>
                    <p class="text-white">You’re all signed up! From now on, you’ll receive a monthly email packed with industry news, fresh job opportunities, and the latest trends from your sector. We hope you find it helpful — and thank you for staying connected with us!</p>
                    <p class="text-white">You can unsubscribe at any time using the link at the bottom of each email.</p>
                    <p class="text-white"><strong>P.S.</strong> Want to stay even more in the loop? We post new roles daily on LinkedIn — follow us <a class="transition duration-300 !underline hover:opacity-50" href="https://www.linkedin.com/company/carrot-pharma-recruitment/" target="_blank">here</a></p>`;
                    break;
                case 11162:
                    message = `<p class='text-white'><strong>Thanks for Referring a Friend!</strong></p>
                    <p class="text-white">We really appreciate you recommending someone to our referral scheme. If your friend is successfully placed in one of our roles and accepts an offer, we’ll be in touch to arrange your £200 voucher.</p>
                    <p class="text-white">Just a quick note: the voucher will be sent once your referral has started their new role and passed their probation period (if applicable).</p>
                    <p class="text-white">Thanks again for getting in touch — we look forward to speaking with you soon!</p>
                    <p class="text-white"><strong>P.S.</strong> Want to stay updated on our latest roles? You can subscribe to our monthly job alerts <a class="transition duration-300 !underline hover:opacity-50" href="${siteUrl}/blog#newsletter">here</a></p>`;
                    break;
                case 11226:
                    message = `<p class='text-white'>Thank you!</p>
                    <p class="text-white">We’ve received your role submission and really appreciate you getting in touch. One of our team will be in touch very soon to learn more about the kind of person you're looking to hire and how we can best support you in growing your team.</p>
                    <p class="text-white">Thanks again for connecting.</p>
                    <p class="text-white">The Carrot team</p>`;
                    break;
                default:
                    message = `<p class='text-white'>Thank you for getting in touch. We will get back to you shortly.</p>`;
                    break;
            }
            
            modalBody.innerHTML = message;
        }
    });

    document.addEventListener('wpcf7mailfailed', function (event) {
        const modalBody = document.querySelector(`#${event.detail.unitTag}`);
        if (modalBody) {
            modalBody.innerHTML += "<p class='text-red-500'>There was an error submitting the form. Please try again.</p>";
        }
    });

    document.addEventListener('wpcf7beforesubmit', function (e) {
        const form = e.target;
        const submitBtn = form.querySelector('input[type="submit"], button[type="submit"]');
        if (submitBtn) {
            submitBtn.disabled = true;
            submitBtn.classList.add('disabled');
            submitBtn.dataset.originalText = submitBtn.value;
            submitBtn.value = 'Sending...';
        }
    });

    document.addEventListener('wpcf7submit', function (e) {
        const form = e.target;
        const submitBtn = form.querySelector('input[type="submit"], button[type="submit"]');
        if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.classList.remove('disabled');
            submitBtn.value = submitBtn.dataset.originalText || 'Submit';
        }
    });
});
 */
